import Common from '@/libs/common';
import type { Shape } from '@/@types/gtfs-ressource';
import { MAX_ACCESS_WALK_M, MIN_TRANSFER_SECONDS, walkSeconds, type Network } from './network';
import { buildTimetable, raptor, type Access, type Direction, type RaptorJourney } from './raptor';
import type { WalkQuote, WalkRequest } from '@/libs/walking';
import {
  buildRealtimeOverlay,
  isStopServed,
  stopDelay,
  tripRealtime,
  type RealtimeSnapshot,
} from './realtime';

/** Ranking weights: tune them here after trials on real networks. */
export const RANKING = {
  transferPenaltyMinutes: 5,
  walkMinuteWeight: 1.5,
};
export const MAX_ROUNDS = 4;
export const MAX_JOURNEYS = 5;
/** Found before the walks are measured: some of them will not stand the real walk. V1 value (#278), to tune after trials. */
export const MAX_CANDIDATES = 8;
/** Below this, one more search follows the measured walks before the journeys are shown. */
export const MIN_JOURNEYS = 3;
/** As the crow flies: closer than this, walking the whole way is offered next to the buses (validated with product, #278). */
export const MAX_DIRECT_WALK_M = 2000;
/** Searches run one after the other, each just past the previous first departure (rRAPTOR). */
const MAX_SEARCHES = 8;

export type JourneyMode = 'depart' | 'arrive';

export interface LatLon {
  lat: number;
  lon: number;
}

/** Every quay of a stop, reached without walking, or a place the passenger walks to or from (2 km at most). */
export type Endpoint =
  | {
      kind: 'stops';
      stopIds: string[];
      /** Where the quays stand, served today or not: walking the whole way needs them all. */
      quays?: Array<LatLon & { stopId: string }>;
    }
  | ({ kind: 'position' } & LatLon);

export interface JourneyQuery {
  origin: Endpoint;
  destination: Endpoint;
  mode: JourneyMode;
  /** Earliest departure (`depart`) or latest arrival (`arrive`), in epoch seconds. */
  time: number;
  realtime?: RealtimeSnapshot;
  shapes: { [shapeId: string]: Shape };
}

export interface WalkLeg {
  type: 'walk';
  fromStopId?: string;
  toStopId?: string;
  from: LatLon;
  to: LatLon;
  departure: number;
  arrival: number;
  duration: number;
  distance: number;
  /** The duration comes from the crow-fly distance: not measured along the streets (yet). */
  estimated: boolean;
  /** [lon, lat] points along the streets, once measured. */
  path?: Array<[number, number]>;
}

export interface TransitLeg {
  type: 'transit';
  tripKey: string;
  tripId: string;
  routeId: string;
  headsign: string;
  boardStopId: string;
  alightStopId: string;
  boardSequence: number;
  alightSequence: number;
  /** Stops called at, boarding and alighting included. */
  stopIds: string[];
  /** Time at each of `stopIds`: the departure from the boarding stop, the arrival at the others. */
  stopTimes: number[];
  departure: number;
  arrival: number;
  realtime: boolean;
  distance: number;
  /** [lon, lat] points of the ride, for the map. */
  path: Array<[number, number]>;
}

export type JourneyLeg = WalkLeg | TransitLeg;

export interface Journey {
  id: string;
  legs: JourneyLeg[];
  departure: number;
  arrival: number;
  transfers: number;
  walkSeconds: number;
  distance: number;
  cost: number;
}

export interface JourneyResults {
  journeys: Journey[];
  /** The one arriving first (`depart`) or leaving last (`arrive`). */
  fastestId?: string;
  /** No stop lies within walking range of the origin. */
  originUnreachable?: boolean;
  /** No stop lies within walking range of the destination. */
  destinationUnreachable?: boolean;
}

/** Stops within walking range of a place, the search's way in (or out). */
export function positionAccess(network: Network, position: LatLon): Access[] {
  const accesses: Access[] = [];
  network.stops.forEach((stop, index) => {
    const distance = Common.coordDistance(
      { latitude: position.lat, longitude: position.lon },
      { latitude: stop.stop_lat, longitude: stop.stop_lon },
    );
    if (distance <= MAX_ACCESS_WALK_M)
      accesses.push({ stop: index, seconds: walkSeconds(distance), distance });
  });
  return accesses;
}

function stopsAccess(network: Network, stopIds: string[]): Access[] {
  return stopIds
    .map(stopId => network.stopIndex.get(stopId))
    .filter((index): index is number => index !== undefined)
    .map(stop => ({ stop, seconds: 0, distance: 0 }));
}

export function endpointAccess(network: Network, endpoint: Endpoint): Access[] {
  return endpoint.kind === 'position'
    ? positionAccess(network, endpoint)
    : stopsAccess(network, endpoint.stopIds);
}

export interface JourneyCandidates extends Omit<JourneyResults, 'fastestId'> {
  /** Where one more search would start, past the candidates found. */
  nextTime: number;
}

/**
 * Journeys that no other one beats, estimated walks included, from successive
 * searches each just past the previous first departure (rRAPTOR).
 */
export function searchCandidates(network: Network, query: JourneyQuery, count: number): JourneyCandidates {
  const direction: Direction = query.mode === 'depart' ? 1 : -1;
  const origin = endpointAccess(network, query.origin);
  const destination = endpointAccess(network, query.destination);
  const walk = directWalk(network, query);
  if (origin.length === 0 || destination.length === 0) {
    if (walk) return { journeys: [walk], nextTime: query.time };
    if (origin.length === 0) return { journeys: [], originUnreachable: true, nextTime: query.time };
    return { journeys: [], destinationUnreachable: true, nextTime: query.time };
  }

  const overlay = query.realtime ? buildRealtimeOverlay(network, query.realtime) : undefined;
  const timetable = buildTimetable(network, overlay);
  const found = new Map<string, Journey>(walk ? [[walk.id, walk]] : []);
  let time = query.time;
  let kept: Journey[] = walk ? [walk] : [];
  for (let search = 0; search < MAX_SEARCHES && kept.length < count; search++) {
    const batch = raptor(network, {
      direction,
      time,
      sources: direction === 1 ? origin : destination,
      targets: direction === 1 ? destination : origin,
      maxRounds: MAX_ROUNDS,
      timetable,
    }).map(raw => toJourney(network, query, raw, direction, overlay));
    if (batch.length === 0) break;
    batch.forEach(journey => found.has(journey.id) || found.set(journey.id, journey));
    // The walk is still an estimate here: it drops rides only in the final ranking, measured when the IGN answered.
    kept = withoutDominated([...found.values()], query.mode, { walkBeatsRides: false });
    time =
      direction === 1
        ? Math.min(...batch.map(journey => journey.departure)) + 1
        : Math.max(...batch.map(journey => journey.arrival)) - 1;
  }

  return { journeys: kept, nextTime: time };
}

/**
 * The journeys on estimated walks, ranked, with no walk measured. The app shows
 * `searchVerifiedJourneys`; this synchronous reference is the one the RAPTOR specs pin.
 */
export function searchJourneys(network: Network, query: JourneyQuery): JourneyResults {
  const { journeys, originUnreachable, destinationUnreachable } = searchCandidates(
    network,
    query,
    MAX_JOURNEYS,
  );
  return {
    ...rankJourneys(withoutDominated(journeys, query.mode), query.mode),
    originUnreachable,
    destinationUnreachable,
  };
}

const pointKey = (point: LatLon) => `${point.lat.toFixed(4)},${point.lon.toFixed(4)}`;

/**
 * Identifies a walk by its ends: a stop by its id, a position or an address by its
 * coordinates to about 10 m, so that a jittering GPS position still finds its walk.
 */
export function walkKey(leg: WalkLeg): string {
  const from = leg.fromStopId ?? pointKey(leg.from);
  const to = leg.toStopId ?? pointKey(leg.to);
  return `${from}>${to}`;
}

/** The estimated walks of the journeys, each asked once however many journeys share it. */
export function walksToVerify(journeys: Journey[]): WalkRequest[] {
  const requests = new Map<string, WalkRequest>();
  journeys.forEach(journey =>
    journey.legs.forEach(leg => {
      if (leg.type !== 'walk' || !leg.estimated || leg.distance === 0) return;
      const key = walkKey(leg);
      if (!requests.has(key)) requests.set(key, { key, from: leg.from, to: leg.to });
    }),
  );
  return [...requests.values()];
}

/** The journey on the walks measured along the streets; walks without a measure keep their estimate. */
export function measureWalks(
  journey: Journey,
  quotes: Map<string, WalkQuote>,
  mode: JourneyMode,
  time: number,
): Journey {
  const legs: JourneyLeg[] = journey.legs.map(leg => {
    const quote = leg.type === 'walk' && leg.estimated ? quotes.get(walkKey(leg)) : undefined;
    if (!quote) return { ...leg };
    return { ...leg, distance: quote.distance, duration: quote.seconds, path: quote.path, estimated: false };
  });
  return { ...finalizeJourney(legs, mode, time), id: journey.id };
}

/**
 * The journey on the measured walks, or undefined when they make it impossible: a
 * connection missed, a departure before the time asked for (`depart`), an arrival
 * after it (`arrive`).
 */
export function applyWalks(
  journey: Journey,
  quotes: Map<string, WalkQuote>,
  mode: JourneyMode,
  time: number,
): Journey | undefined {
  const measured = measureWalks(journey, quotes, mode, time);
  const { legs } = measured;
  const connectionMissed = legs.some((leg, index) => {
    const [previous, next] = [legs[index - 1], legs[index + 1]];
    if (leg.type !== 'walk' || previous?.type !== 'transit' || next?.type !== 'transit') return false;
    return next.departure < previous.arrival + Math.max(leg.duration, MIN_TRANSFER_SECONDS);
  });
  if (connectionMissed) return undefined;
  if (mode === 'depart' && measured.departure < time) return undefined;
  if (mode === 'arrive' && measured.arrival > time) return undefined;
  return measured;
}

/**
 * Drops a journey another one beats: leaving no earlier, arriving no later, with no
 * more transfers. Successive searches find both, e.g. an early bus waiting for the
 * same connection as a later one.
 */
export function withoutDominated(
  journeys: Journey[],
  mode: JourneyMode,
  { walkBeatsRides = true }: { walkBeatsRides?: boolean } = {},
): Journey[] {
  const dominates = (a: Journey, b: Journey) =>
    a.departure >= b.departure &&
    a.arrival <= b.arrival &&
    a.transfers <= b.transfers &&
    (a.departure > b.departure || a.arrival < b.arrival || a.transfers < b.transfers);
  // Walking the whole way leaves whenever the passenger likes: it is never beaten on the
  // departure, and a ride it beats on the time asked for is of no use.
  const walk = journeys.find(isWalkOnly);
  const beatenByWalking = (journey: Journey) =>
    walkBeatsRides &&
    !!walk &&
    (mode === 'depart' ? journey.arrival >= walk.arrival : journey.departure <= walk.departure);
  return journeys.filter(journey =>
    journey === walk
      ? true
      : !beatenByWalking(journey) && !journeys.some(other => other !== walk && dominates(other, journey)),
  );
}

export function isWalkOnly(journey: Journey): boolean {
  return journey.legs.every(leg => leg.type === 'walk');
}

/** The ends of a search as points: every quay of a stop, or the place itself. */
function endpointPoints(network: Network, endpoint: Endpoint): Array<{ point: LatLon; stopId?: string }> {
  if (endpoint.kind === 'position') return [{ point: { lat: endpoint.lat, lon: endpoint.lon } }];
  if (endpoint.quays) {
    return endpoint.quays.map(({ stopId, lat, lon }) => ({ point: { lat, lon }, stopId }));
  }
  return endpoint.stopIds.flatMap(stopId => {
    const index = network.stopIndex.get(stopId);
    if (index === undefined) return [];
    const stop = network.stops[index];
    return [{ point: { lat: stop.stop_lat, lon: stop.stop_lon }, stopId }];
  });
}

/** Walking the whole way, between the closest quays or places, when close enough. */
function directWalk(network: Network, query: JourneyQuery): Journey | undefined {
  let closest:
    | {
        distance: number;
        from: ReturnType<typeof endpointPoints>[number];
        to: ReturnType<typeof endpointPoints>[number];
      }
    | undefined;
  endpointPoints(network, query.origin).forEach(from =>
    endpointPoints(network, query.destination).forEach(to => {
      const distance = Common.coordDistance(
        { latitude: from.point.lat, longitude: from.point.lon },
        { latitude: to.point.lat, longitude: to.point.lon },
      );
      if (!closest || distance < closest.distance) closest = { distance, from, to };
    }),
  );
  if (!closest || closest.distance > MAX_DIRECT_WALK_M) return undefined;
  const walk: WalkLeg = {
    type: 'walk',
    fromStopId: closest.from.stopId,
    toStopId: closest.to.stopId,
    from: closest.from.point,
    to: closest.to.point,
    departure: 0,
    arrival: 0,
    duration: walkSeconds(closest.distance),
    distance: closest.distance,
    estimated: true,
  };
  return finalizeJourney([walk], query.mode, query.time);
}

/**
 * Up to MAX_JOURNEYS journeys by cost; « Arriver à » lists them from the latest
 * departure down, the order the passenger picks from.
 */
export function rankJourneys(journeys: Journey[], mode: JourneyMode): JourneyResults {
  const byCost = [...journeys].sort((a, b) => a.cost - b.cost || a.arrival - b.arrival);
  const kept = byCost.slice(0, MAX_JOURNEYS);
  if (mode === 'arrive') kept.sort((a, b) => b.departure - a.departure || a.cost - b.cost);

  const fastest = [...kept].sort((a, b) =>
    mode === 'depart'
      ? a.arrival - b.arrival || a.cost - b.cost
      : b.departure - a.departure || a.cost - b.cost,
  )[0];
  return { journeys: kept, fastestId: fastest?.id };
}

/**
 * Minutes on the way from the time asked for — the wait before leaving included, so
 * that a short ride two hours later does not outrank the next bus — plus a penalty
 * per transfer and a weight on every minute walked.
 */
export function journeyCost(journey: Omit<Journey, 'cost' | 'id'>, mode: JourneyMode, time: number): number {
  const minutes = (mode === 'depart' ? journey.arrival - time : time - journey.departure) / 60;
  return (
    minutes +
    RANKING.transferPenaltyMinutes * journey.transfers +
    (RANKING.walkMinuteWeight * journey.walkSeconds) / 60
  );
}

function toJourney(
  network: Network,
  query: JourneyQuery,
  raw: RaptorJourney,
  direction: Direction,
  overlay: ReturnType<typeof buildRealtimeOverlay> | undefined,
): Journey {
  // Backward, the scan runs from the destination: flip it into time order.
  const steps = direction === 1 ? raw.legs : [...raw.legs].reverse();
  const [originAccessStep, destinationAccessStep] =
    direction === 1 ? [raw.source, raw.target] : [raw.target, raw.source];
  const stopPoint = (index: number): LatLon => ({
    lat: network.stops[index].stop_lat,
    lon: network.stops[index].stop_lon,
  });

  const legs: JourneyLeg[] = [];
  steps.forEach(step => {
    if (step.kind === 'walk') {
      const [from, to] = direction === 1 ? [step.from, step.to] : [step.to, step.from];
      legs.push({
        type: 'walk',
        fromStopId: network.stopIds[from],
        toStopId: network.stopIds[to],
        from: stopPoint(from),
        to: stopPoint(to),
        departure: 0,
        arrival: 0,
        duration: step.footpath.walkSeconds,
        distance: step.footpath.distance,
        estimated: true,
      });
      return;
    }
    const pattern = network.patterns[step.pattern];
    const trip = pattern.trips[step.trip];
    const times = overlay?.get(trip.key);
    const [board, alight] = direction === 1 ? [step.from, step.to] : [step.to, step.from];
    const stopTimes = trip.trip.stop_times;
    const patternStopIds = pattern.stops.map(stop => network.stopIds[stop]);
    const stopIds = patternStopIds.slice(board, alight + 1);
    const timed = times ?? trip;
    const ride = ridePath(network, patternStopIds, board, alight, query.shapes[trip.trip.shape_id]);
    legs.push({
      type: 'transit',
      tripKey: trip.key,
      tripId: trip.trip.trip_id,
      routeId: trip.trip.route_id,
      headsign: trip.trip.trip_headsign,
      boardStopId: network.stopIds[pattern.stops[board]],
      alightStopId: network.stopIds[pattern.stops[alight]],
      boardSequence: stopTimes[board].stop_sequence,
      alightSequence: stopTimes[alight].stop_sequence,
      stopIds,
      stopTimes: stopIds.map((_, i) => (i === 0 ? timed.departures[board] : timed.arrivals[board + i])),
      departure: timed.departures[board],
      arrival: timed.arrivals[alight],
      realtime: Boolean(times?.realtime),
      distance: ride.distance,
      path: ride.path,
    });
  });

  if (query.origin.kind === 'position' && originAccessStep.seconds > 0) {
    const firstStop = network.stopIds[originAccessStep.stop];
    legs.unshift({
      type: 'walk',
      toStopId: firstStop,
      from: { lat: query.origin.lat, lon: query.origin.lon },
      to: stopPoint(originAccessStep.stop),
      departure: 0,
      arrival: 0,
      duration: originAccessStep.seconds,
      distance: originAccessStep.distance,
      estimated: true,
    });
  }

  if (query.destination.kind === 'position' && destinationAccessStep.seconds > 0) {
    legs.push({
      type: 'walk',
      fromStopId: network.stopIds[destinationAccessStep.stop],
      from: stopPoint(destinationAccessStep.stop),
      to: { lat: query.destination.lat, lon: query.destination.lon },
      departure: 0,
      arrival: 0,
      duration: destinationAccessStep.seconds,
      distance: destinationAccessStep.distance,
      estimated: true,
    });
  }

  return finalizeJourney(legs, query.mode, query.time);
}

/** Lays the walks against the rides around them and derives the journey's totals. */
function finalizeJourney(legs: JourneyLeg[], mode: JourneyMode, time: number): Journey {
  timeWalks(legs, mode, time);
  const transits = legs.filter((leg): leg is TransitLeg => leg.type === 'transit');
  const walks = legs.filter((leg): leg is WalkLeg => leg.type === 'walk');
  const totals = {
    legs,
    departure: legs[0].departure,
    arrival: legs[legs.length - 1].arrival,
    transfers: Math.max(0, transits.length - 1),
    walkSeconds: walks.reduce((sum, leg) => sum + leg.duration, 0),
    distance: legs.reduce((sum, leg) => sum + leg.distance, 0),
  };
  return {
    ...totals,
    id:
      transits.length > 0
        ? transits.map(leg => `${leg.tripKey}:${leg.boardSequence}-${leg.alightSequence}`).join('>')
        : 'walk',
    cost: journeyCost(totals, mode, time),
  };
}

/**
 * A walk starts as the ride before it ends; the walk to the first stop ends as that ride
 * leaves. Walking the whole way starts at the time asked for, or ends at it (`arrive`).
 */
function timeWalks(legs: JourneyLeg[], mode: JourneyMode, time: number) {
  if (legs.length === 1 && legs[0].type === 'walk') {
    const [walk] = legs;
    walk.departure = mode === 'depart' ? time : time - walk.duration;
    walk.arrival = walk.departure + walk.duration;
    return;
  }
  legs.forEach((leg, index) => {
    if (leg.type !== 'walk') return;
    const previous = legs[index - 1];
    if (previous) {
      leg.departure = previous.arrival;
      leg.arrival = previous.arrival + leg.duration;
    } else {
      leg.arrival = legs[index + 1]?.departure ?? 0;
      leg.departure = leg.arrival - leg.duration;
    }
  });
}

function pointDistance(a: [number, number], b: [number, number]): number {
  return Common.coordDistance({ latitude: a[1], longitude: a[0] }, { latitude: b[1], longitude: b[0] });
}

/** Flat approximation, enough to rank the vertices of a shape by their distance to a stop. */
function planarDistance(a: [number, number], b: [number, number]): number {
  const dx = (a[0] - b[0]) * Math.cos((a[1] * Math.PI) / 180);
  return Math.hypot(dx, a[1] - b[1]);
}

/** Weight of a stop's gap to its vertex against the length ridden along the shape. */
const STOP_GAP_WEIGHT = 10;

/**
 * The vertex each stop of a pattern lies on, never before the previous stop's. Nearest vertex
 * alone mistakes the way back of an out-and-back or loop shape passing by a stop for the way
 * out. Every stop is matched at once, minimising the gaps to the stops and the length ridden:
 * a stop matched on a later pass costs the whole detour, a stop missed costs its gap tenfold.
 */
function matchStopsOnShape(coordinates: Array<[number, number]>, points: Array<[number, number]>): number[] {
  const along = [0];
  for (let v = 1; v < coordinates.length; v++) {
    along.push(along[v - 1] + planarDistance(coordinates[v - 1], coordinates[v]));
  }
  const gap = (v: number, k: number) => STOP_GAP_WEIGHT * planarDistance(coordinates[v], points[k]);

  // costs[v]: best cost with the current stop on vertex v, less the length along the shape up to v.
  let costs = coordinates.map((_, v) => gap(v, 0) - along[v]);
  const bestBefore: Int32Array[] = [];
  for (let k = 1; k < points.length; k++) {
    const best = new Int32Array(coordinates.length);
    const next = new Array<number>(coordinates.length);
    let bestIndex = 0;
    for (let v = 0; v < coordinates.length; v++) {
      if (costs[v] < costs[bestIndex]) bestIndex = v;
      best[v] = bestIndex;
      next[v] = costs[bestIndex] + gap(v, k);
    }
    bestBefore.push(best);
    costs = next;
  }
  let last = 0;
  for (let v = 1; v < coordinates.length; v++) {
    if (costs[v] + along[v] < costs[last] + along[last]) last = v;
  }
  const vertices = new Array<number>(points.length);
  vertices[points.length - 1] = last;
  for (let k = points.length - 1; k > 0; k--) vertices[k - 1] = bestBefore[k - 1][vertices[k]];
  return vertices;
}

const stopVerticesCache = new WeakMap<Shape, Map<string, number[]>>();

function stopVertices(shape: Shape, stopIds: string[], points: Array<[number, number]>): number[] {
  let byPattern = stopVerticesCache.get(shape);
  if (!byPattern) stopVerticesCache.set(shape, (byPattern = new Map()));
  const key = stopIds.join('|');
  let vertices = byPattern.get(key);
  if (!vertices) byPattern.set(key, (vertices = matchStopsOnShape(shape.geometry.coordinates, points)));
  return vertices;
}

function pathLength(path: Array<[number, number]>): number {
  let length = 0;
  for (let i = 1; i < path.length; i++) length += pointDistance(path[i - 1], path[i]);
  return length;
}

/**
 * The portion of the trip's shape between two stops of its pattern, or the straight line between
 * its stops without one.
 */
export function ridePath(
  network: Network,
  patternStopIds: string[],
  board: number,
  alight: number,
  shape: Shape | undefined,
): { path: Array<[number, number]>; distance: number } {
  const points = patternStopIds.map(stopId => {
    const stop = network.stops[network.stopIndex.get(stopId)!];
    return [stop.stop_lon, stop.stop_lat] as [number, number];
  });
  const ridden = points.slice(board, alight + 1);
  const coordinates = shape?.geometry.coordinates;
  if (!shape || !coordinates || coordinates.length < 2) return { path: ridden, distance: pathLength(ridden) };

  const vertices = stopVertices(shape, patternStopIds, points);
  const [start, end] = [vertices[board], vertices[alight]];
  if (end <= start) return { path: ridden, distance: pathLength(ridden) };
  const path = [points[board], ...coordinates.slice(start, end + 1), points[alight]];
  return { path, distance: pathLength(path) };
}

export type TrackingIssue = 'canceled' | 'skippedStop' | 'missedConnection';

export interface TrackedJourney {
  journey: Journey;
  /** Why the journey can no longer be made, if it cannot. */
  issue?: TrackingIssue;
}

/**
 * The chosen journey on the latest live data, without searching again: the rides
 * take their estimated times, and a ride leaving before the passenger can be at
 * its stop breaks the journey.
 */
export function trackJourney(
  network: Network,
  journey: Journey,
  snapshot: RealtimeSnapshot,
  mode: JourneyMode,
  time: number,
): TrackedJourney {
  let issue: TrackingIssue | undefined;
  const legs: JourneyLeg[] = journey.legs.map(leg => {
    if (leg.type === 'walk') return { ...leg };
    const located = network.tripsByKey.get(leg.tripKey);
    if (!located) return { ...leg };
    const trip = network.patterns[located.pattern].trips[located.index];
    const realtime = tripRealtime(trip, snapshot);
    if (realtime?.tripUpdate?.is_canceled) issue ??= 'canceled';
    if (!isStopServed(leg.boardSequence, realtime) || !isStopServed(leg.alightSequence, realtime)) {
      issue ??= 'skippedStop';
    }
    const stopTimes = trip.trip.stop_times;
    const board = stopTimes.findIndex(st => st.stop_sequence === leg.boardSequence);
    const alight = stopTimes.findIndex(st => st.stop_sequence === leg.alightSequence);
    const calls = stopTimes.slice(board, alight + 1);
    return {
      ...leg,
      stopTimes: calls.map(
        (st, i) =>
          (i === 0 ? trip.departures[board] : trip.arrivals[board + i]) +
          (stopDelay(st.stop_sequence, realtime) ?? 0),
      ),
      departure: trip.departures[board] + (stopDelay(leg.boardSequence, realtime) ?? 0),
      arrival: trip.arrivals[alight] + (stopDelay(leg.alightSequence, realtime) ?? 0),
      realtime: stopDelay(leg.boardSequence, realtime) !== undefined,
    };
  });

  // Same rule as the search: a change takes the walk, and never less than the minimum transfer time.
  let readyAt: number | undefined;
  let transferWalk = 0;
  legs.forEach(leg => {
    if (leg.type === 'walk') {
      transferWalk += leg.duration;
      return;
    }
    if (readyAt !== undefined && leg.departure < readyAt + Math.max(transferWalk, MIN_TRANSFER_SECONDS)) {
      issue ??= 'missedConnection';
    }
    readyAt = leg.arrival;
    transferWalk = 0;
  });

  return { journey: { ...finalizeJourney(legs, mode, time), id: journey.id }, issue };
}
